import { memo, useCallback } from "react";
import { cn } from "src/utilities/shadcnUtils";

// Shared pill geometry for the toggle family. The binary `Toggle` (here) and
// the `ToggleTriState` both render through `Pill` so they look identical — the
// only difference is whether the centre (unset) rest position is reachable.
export const TRACK_W = 46;
export const TRACK_H = 20;
const THUMB = 16;
const PAD = 2;

const thumbX = (value?: boolean): number => {
  if (value === true) return TRACK_W - THUMB - PAD;
  if (value === false) return PAD;
  return (TRACK_W - THUMB) / 2;
};

const trackClass = (value?: boolean): string => {
  if (value === true) return "bg-green-500 border-green-500";
  if (value === false) return "bg-zinc-400 border-zinc-400 dark:bg-zinc-600 dark:border-zinc-600";
  // unset — hollow + outlined so it clearly reads as "neither".
  return "bg-transparent border-zinc-300 dark:border-zinc-600";
};

const thumbClass = (value?: boolean): string =>
  value === undefined ? "bg-zinc-400 dark:bg-zinc-500" : "bg-white";

export interface PillProps {
  value?: boolean;
  onClick?: () => void;
  interactive: boolean;
}

/** Shared visual for the toggle family — a track with a positioned thumb. */
export const Pill = ({ value, onClick, interactive }: PillProps) => {
  const inner = (
    <>
      <div
        className={cn("absolute rounded-full border box-border transition-colors", trackClass(value))}
        style={{ left: 0, width: TRACK_W, height: TRACK_H, top: "50%", marginTop: -TRACK_H / 2 }}
      />
      <div
        className={cn("absolute rounded-full shadow-sm transition-all", thumbClass(value))}
        style={{
          width: THUMB,
          height: THUMB,
          top: "50%",
          marginTop: -THUMB / 2,
          transform: `translateX(${thumbX(value)}px)`,
        }}
      />
    </>
  );

  if (!interactive) {
    return (
      <div className="relative inline-flex shrink-0 items-center" style={{ width: TRACK_W, height: TRACK_H }}>
        {inner}
      </div>
    );
  }

  return (
    <button
      type="button"
      aria-label="Toggle"
      onClick={onClick}
      className="relative inline-flex shrink-0 cursor-pointer items-center border-0 bg-transparent p-0"
      style={{ width: TRACK_W, height: TRACK_H }}
    >
      {inner}
    </button>
  );
};

export interface ToggleProps {
  value?: boolean;
  onChange?: (value: boolean) => void;
  disabled?: boolean;
}

/**
 * Binary on/off toggle. For a nullable boolean where "not set" must stay
 * distinct from "false", use `ToggleTriState` (same styling, adds a centre
 * rest position).
 */
const Toggle = ({ value, onChange, disabled }: ToggleProps) => {
  const toggle = useCallback(() => onChange?.(!value), [onChange, value]);
  return <Pill value={!!value} onClick={toggle} interactive={!disabled && !!onChange} />;
};

export default memo(Toggle);
